import { CAPSTONE_CRITERIA } from '../presets';
import { finalWindowMeanError, overshoot, settlingTime } from '../../simulation/metrics';
import { expandTrace, type RunRecord } from '../../state/runRecord';
import type { Sample } from '../../simulation/types';
import type { GateContext } from '../types';

export const f = (x: number, digits = 2) => {
  const v = Number(x.toFixed(digits));
  return (Object.is(v, -0) ? 0 : v).toFixed(digits);
};

export const run = (ctx: GateContext, stepId: string): RunRecord => {
  const r = ctx.runs[stepId];
  if (!r) throw new Error(`Missing evidence for ${stepId}`);
  return r;
};

export const samples = (ctx: GateContext, stepId: string): Sample[] => expandTrace(run(ctx, stepId).trace);

/** Find the first stored sample satisfying a predicate (after tMin). */
export function findSample(ctx: GateContext, stepId: string, pred: (s: Sample, prev: Sample | null) => boolean, tMin = 0): Sample | null {
  const xs = samples(ctx, stepId);
  for (let i = 0; i < xs.length; i++) {
    if (xs[i].t < tMin) continue;
    if (pred(xs[i], i > 0 ? xs[i - 1] : null)) return xs[i];
  }
  return null;
}

/** Index-based pair lookup (previous stored sample and current stored sample). */
export function findPair(ctx: GateContext, stepId: string, pred: (s: Sample, prev: Sample) => boolean, tMin = 0) {
  const xs = samples(ctx, stepId);
  for (let i = 1; i < xs.length; i++) if (xs[i].t >= tMin && pred(xs[i], xs[i - 1])) return { prev: xs[i - 1], cur: xs[i] };
  return null;
}

export function lastSample(ctx: GateContext, stepId: string): Sample {
  const xs = samples(ctx, stepId);
  return xs[xs.length - 1];
}

export function peak(ctx: GateContext, stepId: string, tMin = 0): Sample {
  return samples(ctx, stepId)
    .filter((s) => s.t >= tMin)
    .reduce((a, b) => (b.z > a.z ? b : a));
}

export function finalError(r: RunRecord, window = 2): number {
  return finalWindowMeanError(expandTrace(r.trace), window).value ?? NaN;
}

/** First reference step of a run (time, from, to), if any. */
export function firstStep(r: RunRecord) {
  const s = r.config.reference.steps[0];
  return s ? { t: s.t, from: r.config.reference.initial, to: s.value } : null;
}

export interface CapstoneResult {
  pass: boolean;
  overshoot: number | null;
  settleStep: number | null;
  settleDisturbance: number | null;
  saturation: number;
  reasons: string[];
}

/** Evaluate the published capstone criteria on a run. Step and disturbance windows are kept separate. */
export function evaluateCapstone(r: RunRecord): CapstoneResult {
  const C = CAPSTONE_CRITERIA;
  const trace = expandTrace(r.trace);
  const tDist = r.config.disturbance.steps[0]?.t ?? r.config.duration;
  const step = firstStep(r) ?? { t: C.stepTime, from: 2, to: 2.5 };
  const tEnd = r.config.duration;
  const stepW = { tStart: step.t, tEnd: tDist, from: step.from, to: step.to, tolerance: C.tolerance };
  const distW = { tStart: tDist, tEnd, from: step.to, to: step.to, tolerance: C.tolerance };
  const os = overshoot(trace, stepW);
  const s1 = settlingTime(trace, stepW);
  const s2 = settlingTime(trace, distW);
  const reasons: string[] = [];
  if (r.status !== 'complete') reasons.push('The run did not complete.');
  if (os.value === null || os.value >= C.maxOvershoot)
    reasons.push(`Overshoot ${os.value === null ? 'unavailable' : f(os.value * 100, 1) + ' %'}. Must be below ${C.maxOvershoot * 100} %.`);
  if (s1.value === null || s1.value > C.settleWithin)
    reasons.push(`After the step: ${s1.value === null ? 'not settled' : 'settled in ' + f(s1.value) + ' s'}. Must settle within ±${C.tolerance} m in ${C.settleWithin} s.`);
  if (s2.value === null || s2.value > C.settleWithin)
    reasons.push(`After the load: ${s2.value === null ? 'not settled' : 'settled in ' + f(s2.value) + ' s'}. Must settle within ±${C.tolerance} m in ${C.settleWithin} s.`);
  if (r.stats.saturationTime > C.maxSaturation)
    reasons.push(`Saturated for ${f(r.stats.saturationTime)} s. At most ${C.maxSaturation} s allowed.`);
  return {
    pass: reasons.length === 0,
    overshoot: os.value,
    settleStep: s1.value,
    settleDisturbance: s2.value,
    saturation: r.stats.saturationTime,
    reasons,
  };
}

/** Evidence check: no manual changes (so the preset’s inputs are what was recorded). */
export const noManualChanges = (r: RunRecord) =>
  r.manualEvents.length > 0 ? 'This step uses the preset inputs. Start a new run without changing the manual input.' : null;
